import React from 'react'
import {
  StyledType,
  SystemIcon,
  borderRadius,
  colors,
  createComponent,
  styled,
  systemIconStyles,
  Text
} from '@workday/canvas-kit-react'
import { extLinkIcon } from '@workday/canvas-system-icons-web'

export interface PPHyperlinkProps {
  href?: string
  children?: React.ReactNode
}

const PPAnchor = styled('a')<PPHyperlinkProps & StyledType>({
  textDecoration: 'none',
  color: colors.toothpaste400,
  cursor: 'pointer',
  borderRadius: borderRadius.s,
  display: 'inline-block',
  padding: '0 2px',
  margin: '0 -2px',
  transition: 'color 0.15s,background-color 0.15s',
  '&:hover': {
    color: colors.toothpaste500,
    background: colors.soap300
  },
  '&:active': {
    color: colors.toothpaste600,
    background: colors.soap400
  }
})

/**
 * `Hyperlink`s should be used when you want to navigate away from the current page or to an anchor
 * on the current page.
 */
export const PPHyperlink = createComponent('a')({
  displayName: 'Hyperlink',
  Component: ({ children, ...elemProps }: PPHyperlinkProps, ref, Element) => (
    <Text fontWeight="medium">
      <PPAnchor ref={ref} as={Element} {...elemProps}>
        {children}
      </PPAnchor>
    </Text>
  )
})

export interface PPExternalHyperlinkProps extends PPHyperlinkProps {
  /**
   * Informs a screen reader user the link will open in a new window. It is read after the link text.
   * This value will need to be translated.
   * @default 'Opens link in new window'
   */
  iconLabel?: string
}

const iconStyles = {
  ...systemIconStyles({ fill: 'currentColor', fillHover: 'currentColor' })
}

const Anchor = styled(PPHyperlink)<PPExternalHyperlinkProps & StyledType>({
  ...iconStyles,
  display: 'inline-flex',
  flexDirection: 'row',
  alignItems: 'center'
})

const iconSize = '1.25em'
const minIconSize = '16px'

const StyledSystemIcon = styled(SystemIcon)<StyledType>({
  ...iconStyles,
  width: `calc(${iconSize} - 1px)`,
  minWidth: `calc(${minIconSize} - 1px)`,
  marginLeft: '2px'
})

/**
 * `ExternalHyperlink`s also have an `inverse` variant. Use this variant when you need to place the
 * link on a dark or colorful background such as `blueberry400`.
 */
export const PPExternalHyperlink = createComponent('a')({
  displayName: 'ExternalHyperlink',
  Component: ({ children, iconLabel = 'Opens link in new window', ...elemProps }: PPExternalHyperlinkProps, ref) => (
    <Text fontWeight="medium">
      <Anchor ref={ref} target="_blank" rel="noreferrer" {...elemProps}>
        <span>{children}</span>
        <StyledSystemIcon
          icon={extLinkIcon}
          role="img"
          aria-label={iconLabel}
          size={iconSize}
          styles={{ ['& svg']: { minWidth: minIconSize, minHeight: minIconSize } }}
        />
      </Anchor>
    </Text>
  )
})
